// src/pages/Profile.jsx
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Container,
  Paper,
  Stack,
  Avatar,
  Button,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Collapse,
  CircularProgress,
} from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import LogoutIcon from "@mui/icons-material/Logout";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SecurityIcon from "@mui/icons-material/Security";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import AddIcon from "@mui/icons-material/Add";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import StadiumIcon from "@mui/icons-material/Stadium";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import VideocamIcon from "@mui/icons-material/Videocam";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WorkspacePremiumRounded from "@mui/icons-material/WorkspacePremiumRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { followService, pageService, profileService } from "../services/resources.service";
import LoadingSpinner from "../components/LoadingSpinner";
import { formatFollowerCount, getMessage, useLocale } from "../i18n/locale";
import { PROFILE_PAGE_SETUP } from "../services/profilePageSetup";
import { clearSessionToken } from "../services/authSession";
import CitySelect from "../components/CitySelect";
import ProFeatureControl from "../components/ProFeatureControl";
import { canJoinProWaitlist, hasProAccount, profileLimitFor } from "../services/proFeatures";
import { BANNER_IMAGE_FALLBACK, getBannerImage, getProfileImage, imageFallbackHandler, PROFILE_IMAGE_FALLBACK } from "../services/profileImages";
import AppVersion from "../components/AppVersion";

const PROFILE_PAGE_ICONS = {
  ARTIST: MusicNoteIcon,
  VENUE: StadiumIcon,
  STUDIO: HeadphonesIcon,
  MEDIA: VideocamIcon,
};

async function fetchMe() {
  return profileService.me();
}

async function updateMe(patch) {
  return profileService.update(patch);
}

function signOut() {
  clearSessionToken();
}

// ---------- Small UI helpers ----------
function RoleChip({ role, locale }) {
  const map = {
    GUEST: { icon: <FavoriteBorderIcon />, color: "default" },
    ARTIST: { icon: <MusicNoteIcon />, color: "primary" },
    VENUE: { icon: <StadiumIcon />, color: "secondary" },
    STUDIO: { icon: <HeadphonesIcon />, color: "default" },
    MEDIA: { icon: <VideocamIcon />, color: "default" },
    ADMIN: { icon: <AdminPanelSettingsIcon />, color: "warning" },
  };
  const m = map[role];
  if (!m) return null;
  return <Chip size="small" color={m.color} icon={m.icon} label={getMessage(locale, `accountRole${role}`)} />;
}

function PageCard({ kind, page, onOpen, onEdit, onDelete, deleting, onError }) {
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followers, setFollowers] = useState(null);
  const [followersLoading, setFollowersLoading] = useState(false);
  const Icon =
    kind === "artist" ? MusicNoteIcon : kind === "venue" ? StadiumIcon : HeadphonesIcon;
  const media = getBannerImage(page);
  const followerLabel = formatFollowerCount(page.followerCount || 0, page.locale);
  const toggleFollowers = async () => {
    if (followersLoading) return;
    if (followersOpen) {
      setFollowersOpen(false);
      return;
    }
    setFollowersOpen(true);
    if (followers !== null) return;
    setFollowersLoading(true);
    try {
      setFollowers(await followService.listForPage(kind, page.id));
    } catch {
      setFollowersOpen(false);
      onError?.();
    } finally {
      setFollowersLoading(false);
    }
  };
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%", display: "flex", flexDirection: "column" }}>
      <img
          src={media}
          onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
          alt={page.name}
          loading="lazy"
          style={{ width: "100%", height: 120, objectFit: "cover", borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
        />
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar src={getProfileImage(page)} imgProps={{ onError: imageFallbackHandler(PROFILE_IMAGE_FALLBACK) }} sx={{ width: 36, height: 36 }}>
            <Icon fontSize="small" />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {page.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {page.city}
            </Typography>
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {followerLabel}
        </Typography>
        <Button
          size="small"
          endIcon={followersLoading ? <CircularProgress size={14} /> : <ExpandMoreIcon sx={{ transform: followersOpen ? "rotate(180deg)" : "none" }} />}
          onClick={toggleFollowers}
          disabled={followersLoading}
          aria-expanded={followersOpen}
        >
          {getMessage(page.locale, "managedPageViewFollowers")}
        </Button>
        <Collapse in={followersOpen}>
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {followers?.length ? followers.map((follower) => (
              <Stack key={follower.id} direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                  {follower.displayName?.slice(0, 1) || "?"}
                </Avatar>
                <Typography variant="body2">
                  {follower.displayName || getMessage(page.locale, "managedPageFollowerAnonymous")}
                </Typography>
              </Stack>
            )) : !followersLoading && followers !== null ? (
              <Typography variant="caption" color="text.secondary">
                {getMessage(page.locale, "managedPageFollowersEmpty")}
              </Typography>
            ) : null}
          </Stack>
        </Collapse>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button
          variant="outlined"
          endIcon={<OpenInNewIcon />}
          onClick={onOpen}
        >
          {getMessage(page.locale, "managedPageOpen")}
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={onEdit}
          disabled={deleting}
        >
          {getMessage(page.locale, "managedPageEdit")}
        </Button>
        <IconButton
          color="error"
          onClick={onDelete}
          disabled={deleting}
          aria-label={`${getMessage(page.locale, "managedPageDelete")} ${page.name}`}
          sx={{ ml: "auto" }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </CardActions>
    </Card>
  );
}

// ---------- Page ----------
export default function Profile() {
  const navigate = useNavigate();
  const { locale } = useLocale();

  const [me, setMe] = useState(null);
  const [follows, setFollows] = useState([]);
  const [loading, setLoading] = useState(true);

  // editable fields
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  const [email, setEmail] = useState("");

  // notifications
  const [nEmailMsg, setNEmailMsg] = useState(false);
  const [nEmailApps, setNEmailApps] = useState(false);
  const [nEmailReviews, setNEmailReviews] = useState(false);
  const [nPushMsg, setNPushMsg] = useState(false);

  // ui
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const [saving, setSaving] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [deletingPage, setDeletingPage] = useState(false);
  const [proWaitlistOpen, setProWaitlistOpen] = useState(false);
  const [joiningProWaitlist, setJoiningProWaitlist] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [m, followedPages] = await Promise.all([
        fetchMe(),
        followService.list().catch(() => []),
      ]);
      setMe(m);
      setFollows(followedPages);
      if (m) {
        setName(m.name || "");
        setRegion(m.region || "");
        setEmail(m.email || "");
        setNEmailMsg(!!m.notifications?.emailMessages);
        setNEmailApps(!!m.notifications?.emailApplications);
        setNEmailReviews(!!m.notifications?.emailReviews);
        setNPushMsg(!!m.notifications?.pushMessages);
      }
      setLoading(false);
    })();
  }, []);

  const isSignedOut = !me;
  const pageSetup = PROFILE_PAGE_SETUP[me?.role];
  const PageSetupIcon = PROFILE_PAGE_ICONS[me?.role];
  const managedPages = pageSetup ? me?.[pageSetup.pagesKey] || [] : [];

  const managedCount = managedPages.length;
  const isPro = hasProAccount(me);
  const profileLimit = profileLimitFor(me);
  const additionalPageLocked = managedCount >= profileLimit;
  const profileLimitMessage = getMessage(
    locale,
    isPro ? "profileProLimitReached" : "profileFreeLimitReached",
  ).replace("{count}", String(profileLimit));
  const saveProfile = async () => {
    if (!me) return;
    setSaving(true);
    try {
      const updated = await updateMe({ name, region });
      setMe((prev) => ({ ...prev, ...updated }));
      setSnack({ open: true, msg: "Profile saved", severity: "success" });
    } catch (e) {
      setSnack({ open: true, msg: "Failed to save profile", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    if (!me || !isPro) return;
    setSaving(true);
    try {
      const updated = await updateMe({
        notifications: {
          emailMessages: nEmailMsg,
          emailApplications: nEmailApps,
          emailReviews: nEmailReviews,
          pushMessages: nPushMsg,
        },
      });
      setMe((prev) => ({ ...prev, ...updated }));
      setSnack({ open: true, msg: "Notification settings updated", severity: "success" });
    } catch (e) {
      setSnack({ open: true, msg: "Failed to update notifications", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const doSignOut = () => {
    signOut();
    setSnack({ open: true, msg: "Signed out", severity: "info" });
    navigate("/", { replace: true });
  };

  const removeFollow = async (item) => {
    try {
      await followService.remove(item.subjectType, item.subjectId);
      setFollows((current) => current.filter((follow) => follow.id !== item.id));
      setSnack({ open: true, msg: getMessage(locale, "followRemoved"), severity: "success" });
    } catch {
      setSnack({ open: true, msg: getMessage(locale, "followError"), severity: "error" });
    }
  };

  const deleteManagedPage = async () => {
    if (!pageSetup || !pageToDelete) return;
    setDeletingPage(true);
    try {
      await pageService.remove(pageSetup.kind, pageToDelete.id);
      setMe((current) => ({
        ...current,
        [pageSetup.pagesKey]: (current?.[pageSetup.pagesKey] || []).filter(
          (page) => page.id !== pageToDelete.id,
        ),
      }));
      setSnack({
        open: true,
        msg: getMessage(locale, "managedPageDeleteSuccess"),
        severity: "success",
      });
      setPageToDelete(null);
    } catch {
      setSnack({
        open: true,
        msg: getMessage(locale, "managedPageDeleteError"),
        severity: "error",
      });
    } finally {
      setDeletingPage(false);
    }
  };

  const joinProWaitlist = async () => {
    if (!me || joiningProWaitlist) return;
    setJoiningProWaitlist(true);
    try {
      await profileService.joinProWaitlist();
      setProWaitlistOpen(false);
      setSnack({ open: true, msg: getMessage(locale, "proWaitlistSuccess"), severity: "success" });
    } catch (error) {
      const alreadyJoined = error?.code === "PRO_WAITLIST_EXISTS";
      setProWaitlistOpen(false);
      setSnack({
        open: true,
        msg: getMessage(locale, alreadyJoined ? "proWaitlistAlreadyJoined" : "proWaitlistError"),
        severity: alreadyJoined ? "info" : "error",
      });
    } finally {
      setJoiningProWaitlist(false);
    }
  };

  if (loading) {
    return <LoadingSpinner minHeight="100dvh" size={44} />;
  }

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="back" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Profile
          </Typography>
          <Box sx={{ ml: "auto" }}>
            {!isSignedOut && <RoleChip role={me.role} locale={locale} />}
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Signed-out fallback if the session expires while this page is open. */}
        {isSignedOut ? (
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 3, textAlign: "center" }}
          >
            <Stack spacing={1.25} alignItems="center">
              <Avatar sx={{ width: 72, height: 72 }}>
                <PersonIcon />
              </Avatar>
              <Typography variant="h6" fontWeight={900}>
                You’re not signed in
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to manage pages, post gigs, and message others.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ width: "100%", maxWidth: 420 }}>
                <Button
                  fullWidth
                  variant="contained"
                  component={RouterLink}
                  to="/auth?redirect=/profile"
                >
                  Sign in
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  component={RouterLink}
                  to="/auth/signup?redirect=/profile"
                >
                  Create account
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ) : (
          <>
            {/* Account card */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar sx={{ width: 64, height: 64 }}>
                  <PersonIcon fontSize="large" />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Account
                  </Typography>
                  <Typography variant="h6" fontWeight={900} noWrap>
                    {me.name}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <RoleChip role={me.role} locale={locale} />
                    {pageSetup && <Chip size="small" icon={<GroupWorkIcon />} label={`${managedCount} page(s)`} />}
                  </Stack>
                </Box>
                <Tooltip title="Sign out">
                  <IconButton onClick={doSignOut} color="inherit" aria-label="sign out">
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={1.25}>
                    <TextField
                      label="Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="Email"
                      value={email}
                      disabled
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                      helperText="Email changes are not available in the MVP."
                    />
                    <CitySelect
                      name="city"
                      autoComplete="address-level2"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveProfile}
                        disabled={saving}
                      >
                        Save profile
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setName(me.name || "");
                          setRegion(me.region || "");
                        }}
                      >
                        Reset
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 3, height: "100%" }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <NotificationsActiveIcon />
                        <Typography variant="subtitle1" fontWeight={800}>
                          {getMessage(locale, "proNotifications")}
                        </Typography>
                        {!isPro && (
                          <Chip
                            icon={<WorkspacePremiumRounded />}
                            label="Pro"
                            color="secondary"
                            size="small"
                          />
                        )}
                      </Stack>
                      <Tooltip title={!isPro ? getMessage(locale, "proAccountRequired") : ""} arrow>
                        <Stack>
                          <FormControlLabel
                            control={
                              <Switch disabled={!isPro} checked={nEmailMsg} onChange={(e) => setNEmailMsg(e.target.checked)} />
                            }
                            label={getMessage(locale, "notificationEmailMessages")}
                          />
                          <FormControlLabel
                            control={
                              <Switch disabled={!isPro} checked={nEmailApps} onChange={(e) => setNEmailApps(e.target.checked)} />
                            }
                            label={getMessage(locale, "notificationEmailApplications")}
                          />
                          <FormControlLabel
                            control={
                              <Switch disabled={!isPro} checked={nEmailReviews} onChange={(e) => setNEmailReviews(e.target.checked)} />
                            }
                            label={getMessage(locale, "notificationEmailReviews")}
                          />
                          <FormControlLabel
                            control={
                              <Switch disabled={!isPro} checked={nPushMsg} onChange={(e) => setNPushMsg(e.target.checked)} />
                            }
                            label={getMessage(locale, "notificationPushMessages")}
                          />
                          <Button variant="outlined" onClick={saveNotifications} disabled={saving || !isPro}>
                            {getMessage(locale, "notificationSave")}
                          </Button>
                        </Stack>
                      </Tooltip>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>

            {canJoinProWaitlist(me) && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <WorkspacePremiumRounded color="secondary" sx={{ fontSize: 36 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={800}>
                      {getMessage(locale, "profileGetProTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getMessage(locale, "profileGetProDescription")}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<WorkspacePremiumRounded />}
                    onClick={() => setProWaitlistOpen(true)}
                  >
                    {getMessage(locale, "profileGetProAction")}
                  </Button>
                </Stack>
              </Paper>
            )}

            {/* Role-specific managed profiles */}
            {pageSetup && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
                  <PageSetupIcon sx={{ mt: 0.25 }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                      {getMessage(locale, pageSetup.titleKey)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getMessage(locale, pageSetup.descriptionKey)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getMessage(locale, isPro ? "profileProLimitSummary" : "profileFreeLimitSummary")
                        .replace("{count}", String(profileLimit))}
                    </Typography>
                  </Box>
                </Stack>

                <Grid container spacing={2}>
                  {managedPages.map((page) => (
                    <Grid key={page.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                      <PageCard
                        kind={pageSetup.kind}
                        page={{ ...page, locale }}
                        onOpen={() => navigate(`${pageSetup.basePath}/${page.id}`)}
                        onEdit={() => navigate(`${pageSetup.basePath}/${page.id}/edit`)}
                        onDelete={() => setPageToDelete(page)}
                        deleting={deletingPage && pageToDelete?.id === page.id}
                        onError={() => setSnack({ open: true, msg: getMessage(locale, "managedPageFollowersError"), severity: "error" })}
                      />
                    </Grid>
                  ))}

                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                        textAlign: "center",
                      }}
                    >
                      <ProFeatureControl
                        locked={additionalPageLocked}
                        label={getMessage(locale, pageSetup.createKey)}
                        lockedMessage={profileLimitMessage}
                        showProBadge={!isPro}
                      >
                        <Button
                          startIcon={<AddIcon />}
                          variant="outlined"
                          disabled={additionalPageLocked}
                          onClick={() => navigate(`${pageSetup.basePath}/new`)}
                        >
                          {managedCount > 0
                            ? getMessage(locale, `proMultiple${pageSetup.kind[0].toUpperCase()}${pageSetup.kind.slice(1)}Pages`)
                            : getMessage(locale, pageSetup.createKey)}
                        </Button>
                      </ProFeatureControl>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FavoriteIcon color="error" />
                <Box>
                  <Typography variant="subtitle1" fontWeight={800}>
                    {getMessage(locale, "followingTitle")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getMessage(locale, "followingDescription")}
                  </Typography>
                </Box>
              </Stack>
              {follows.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {getMessage(locale, "followingEmpty")}
                </Typography>
              ) : (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {follows.map((item) => (
                    <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                        <CardContent>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Avatar src={item.image}>{item.name?.slice(0, 1)}</Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography fontWeight={800} noWrap>{item.name}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {item.city || getMessage(locale, `followType${item.subjectType}`)}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                        <CardActions>
                          <Button onClick={() => navigate(
                            item.subjectType === "media"
                              ? `/media/${item.subjectId}`
                              : `/${item.subjectType}s/${item.subjectId}`
                          )}>
                            {getMessage(locale, "managedPageOpen")}
                          </Button>
                          <IconButton
                            sx={{ ml: "auto" }}
                            onClick={() => removeFollow(item)}
                            aria-label={`${getMessage(locale, "unfollow")} ${item.name}`}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>

            {/* Security / Admin */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <SecurityIcon />
                    <Typography variant="subtitle1" fontWeight={800}>
                      Security
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Update your password, enable MFA (coming soon), and manage sessions.
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      component={RouterLink}
                      to="/auth/reset"
                    >
                      Change password
                    </Button>
                  </Stack>
                </Paper>
              </Grid>

              {me.role === "ADMIN" && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <AdminPanelSettingsIcon />
                      <Typography variant="subtitle1" fontWeight={800}>
                        Admin
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Moderate reports, manage featured placements, and handle disputes.
                    </Typography>
                    <Button variant="contained" onClick={() => navigate("/admin")}>
                      Open Admin Console
                    </Button>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        )}
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <AppVersion />
        </Stack>
      </Container>

      <Dialog
        open={proWaitlistOpen}
        onClose={joiningProWaitlist ? undefined : () => setProWaitlistOpen(false)}
        aria-labelledby="pro-waitlist-title"
      >
        <DialogTitle id="pro-waitlist-title">
          {getMessage(locale, "proWaitlistTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getMessage(locale, "proWaitlistConfirmation").replace("{email}", me?.email || "")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProWaitlistOpen(false)} disabled={joiningProWaitlist}>
            {getMessage(locale, "proWaitlistCancel")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={joinProWaitlist}
            disabled={joiningProWaitlist}
          >
            {getMessage(locale, joiningProWaitlist ? "proWaitlistJoining" : "proWaitlistConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pageToDelete)}
        onClose={deletingPage ? undefined : () => setPageToDelete(null)}
        aria-labelledby="delete-managed-page-title"
      >
        <DialogTitle id="delete-managed-page-title">
          {getMessage(locale, "managedPageDeleteTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {getMessage(locale, "managedPageDeleteConfirmation").replace(
              "{name}",
              pageToDelete?.name || "",
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPageToDelete(null)} disabled={deletingPage}>
            {getMessage(locale, "managedPageDeleteCancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={deleteManagedPage}
            disabled={deletingPage}
          >
            {getMessage(
              locale,
              deletingPage ? "managedPageDeleting" : "managedPageDeleteConfirm",
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
